"use client";
import { useRef } from "react";
import type { RowData } from "@tanstack/react-table";

import { MoreHorizontal } from "@/components/icons";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/record/action-menu";
import { cn } from "@/lib/utils";
import { withCurrentReturnContext } from "@/lib/navigation/return-context";
import type { TableAction } from "../types";

interface RecordRowActionsProps<TData extends RowData> {
  row: TData;
  actions: TableAction<TData>[];
}

function openDialogs(): Set<Element> {
  return new Set(document.querySelectorAll('[role="dialog"][data-state="open"]'));
}

// WHY: a non-modal EditModal opened by a row action closes when the menu hands focus back to its trigger
function useDialogFocusHandoff() {
  const dialogsBeforeAction = useRef<Set<Element> | null>(null);
  return {
    beforeAction: () => {
      dialogsBeforeAction.current = openDialogs();
    },
    onCloseAutoFocus: (event: Event) => {
      const before = dialogsBeforeAction.current;
      dialogsBeforeAction.current = null;
      if (!before) return;
      if ([...openDialogs()].some((dialog) => !before.has(dialog))) event.preventDefault();
    },
  };
}

export function RecordRowActionSheet<TData extends RowData>({
  row,
  actions,
  open,
  onOpenChange,
}: RecordRowActionsProps<TData> & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const router = useRouter();
  const handoff = useDialogFocusHandoff();
  const visibleActions = actions.filter(
    (action) => !action.show || action.show(row),
  );
  if (visibleActions.length === 0) return null;
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuContent onCloseAutoFocus={handoff.onCloseAutoFocus}>
        {visibleActions.map((action, index) => (
          <div key={action.label}>
            {action.separator && index > 0 ? <DropdownMenuSeparator /> : null}
            <RowActionItem action={action} row={row} router={router} beforeAction={handoff.beforeAction} />
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function RecordRowActions<TData extends RowData>({
  row,
  actions,
}: RecordRowActionsProps<TData>) {
  const router = useRouter();
  const handoff = useDialogFocusHandoff();
  const visibleActions = actions.filter(
    (action) => !action.show || action.show(row),
  );

  if (visibleActions.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="quiet"
          size="icon-sm"
          className="w-9 touch-manipulation [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/row:opacity-100 group-focus-within/row:opacity-100 data-[state=open]:opacity-100 [@media(pointer:coarse)]:opacity-100"
          onClick={(event) => event.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="z-50 w-[min(160px,calc(100vw-2rem))]"
        onCloseAutoFocus={handoff.onCloseAutoFocus}
      >
        {visibleActions.map((action, index) => (
          <div key={action.label}>
            {action.separator && index > 0 ? <DropdownMenuSeparator /> : null}
            <RowActionItem action={action} row={row} router={router} beforeAction={handoff.beforeAction} />
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RowActionItem<TData extends RowData>({
  action,
  row,
  router,
  beforeAction,
}: {
  action: TableAction<TData>;
  row: TData;
  router: ReturnType<typeof useRouter>;
  beforeAction: () => void;
}) {
  return (
    <DropdownMenuItem
      onClick={(event) => {
        event.stopPropagation();
        if (action.href) {
          router.push(withCurrentReturnContext(action.href(row)));
        } else {
          beforeAction();
          action.onClick(row);
        }
      }}
      className={cn(
        action.destructive &&
          "text-destructive-strong focus:text-destructive-strong",
      )}
    >
      {action.icon ? <action.icon className="mr-2 h-4 w-4" /> : null}
      {action.label}
    </DropdownMenuItem>
  );
}
